/**
 * Pure helpers for the Commercial Breaks tab of Advertising Management.
 *
 * The breaks API returns one row per station/hour/day combination, which means a
 * "break" is really a weekly slot. Everything below is side-effect free so the
 * filtering, grouping and summary maths can be unit tested away from MUI.
 */

export const BREAK_DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const BREAK_DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toInt = (value, fallback = 0) => {
  const parsed = parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : fallback;
};

export const dayLabel = (value, short = false) => {
  if (value === null || value === undefined || value === '') return 'Every day';
  const index = toInt(value, -1);
  if (index < 0 || index > 6) return 'Every day';
  return short ? BREAK_DAY_SHORT[index] : BREAK_DAY_LABELS[index];
};

export const formatBreakTime = (hour, minute) =>
  `${String(toInt(hour)).padStart(2, '0')}:${String(toInt(minute)).padStart(2, '0')}`;

export const minutesOfDay = (breakItem) =>
  toInt(breakItem?.hour) * 60 + toInt(breakItem?.minute);

export const stationKey = (value) => (value === null || value === undefined ? '' : String(value));

// The ad scheduler only fills breaks WHERE is_active = true, so a NULL flag is
// inactive on air and must read as Inactive here too.
export const isBreakActive = (breakItem) => Boolean(breakItem?.is_active);

/**
 * @param {Array} breaks raw rows from /api/advertising/breaks
 * @param {{search?: string, stationId?: string, day?: string, status?: string}} filters
 *        day: 'any' (ignore), 'every' (day_of_week is null), or '0'..'6'
 *        status: 'any' (ignore), 'active', or 'inactive'
 * @param {Record<string, string>} stationNames id -> station name
 */
export const filterBreaks = (breaks = [], filters = {}, stationNames = {}) => {
  const search = String(filters.search || '').trim().toLowerCase();
  const stationId = stationKey(filters.stationId);
  const day = filters.day === undefined ? 'any' : String(filters.day);
  const status = String(filters.status || 'any');

  return breaks.filter((breakItem) => {
    if (!breakItem) return false;

    if (stationId && stationKey(breakItem.station_id) !== stationId) return false;

    if (status === 'active' && !isBreakActive(breakItem)) return false;
    if (status === 'inactive' && isBreakActive(breakItem)) return false;

    if (day === 'every') {
      if (breakItem.day_of_week !== null && breakItem.day_of_week !== undefined && breakItem.day_of_week !== '') return false;
    } else if (day !== 'any') {
      if (String(breakItem.day_of_week ?? '') !== day) return false;
    }

    if (!search) return true;

    const haystack = [
      breakItem.name,
      breakItem.break_type,
      stationNames[stationKey(breakItem.station_id)],
      breakItem.station_id,
      formatBreakTime(breakItem.hour, breakItem.minute),
    ]
      .filter((value) => value !== null && value !== undefined)
      .join(' ')
      .toLowerCase();

    return haystack.includes(search);
  });
};

/**
 * Sort breaks for the list view. `time` orders by station, then day, then clock
 * time so a station's schedule reads top-to-bottom like a day.
 */
export const sortBreaks = (breaks = [], { orderBy = 'time', order = 'asc' } = {}, stationNames = {}) => {
  const direction = order === 'desc' ? -1 : 1;
  const compareText = (a, b) => String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true });
  const compareNumber = (a, b) => (Number(a) || 0) - (Number(b) || 0);

  const getValue = (breakItem) => {
    switch (orderBy) {
      case 'station':
        return stationNames[stationKey(breakItem.station_id)] || stationKey(breakItem.station_id);
      case 'day':
        return breakItem.day_of_week === null || breakItem.day_of_week === undefined ? -1 : toInt(breakItem.day_of_week, -1);
      case 'is_active':
        return isBreakActive(breakItem) ? 1 : 0;
      case 'max_duration':
      case 'max_spots':
      case 'priority':
        return Number(breakItem[orderBy]) || 0;
      default:
        return breakItem[orderBy];
    }
  };

  const tieBreak = (a, b) => compareNumber(minutesOfDay(a), minutesOfDay(b)) || compareNumber(a.id, b.id);

  return [...breaks].sort((a, b) => {
    if (orderBy === 'time') {
      return (
        compareText(stationKey(a.station_id), stationKey(b.station_id)) * direction
        || compareNumber(daySortKey(a), daySortKey(b)) * direction
        || compareNumber(minutesOfDay(a), minutesOfDay(b)) * direction
        || compareNumber(a.id, b.id)
      );
    }

    const aVal = getValue(a);
    const bVal = getValue(b);
    const primary = typeof aVal === 'number' && typeof bVal === 'number'
      ? compareNumber(aVal, bVal)
      : compareText(aVal, bVal);

    return primary * direction || tieBreak(a, b);
  });
};

const daySortKey = (breakItem) =>
  breakItem?.day_of_week === null || breakItem?.day_of_week === undefined ? -1 : toInt(breakItem.day_of_week, -1);

/**
 * Group breaks into station + hour buckets for the "By hour" view.
 */
export const groupBreaksByHour = (breaks = [], stationNames = {}) => {
  const groups = new Map();

  breaks.forEach((breakItem) => {
    if (!breakItem) return;
    const hour = toInt(breakItem.hour);
    const key = `${stationKey(breakItem.station_id)}|${hour}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        stationId: breakItem.station_id,
        stationName: stationNames[stationKey(breakItem.station_id)] || `Station ${breakItem.station_id}`,
        hour,
        breaks: [],
        spotCapacity: 0,
        spotCount: 0,
        durationSeconds: 0,
      });
    }
    const group = groups.get(key);
    group.breaks.push(breakItem);
    group.spotCapacity += Number(breakItem.max_spots) || 0;
    group.durationSeconds += Number(breakItem.max_duration) || 0;
  });

  const ordered = [...groups.values()].map((group) => ({
    ...group,
    spotCount: group.breaks.length,
    activeCount: group.breaks.filter(isBreakActive).length,
    breaks: [...group.breaks].sort(
      (a, b) => minutesOfDay(a) - minutesOfDay(b) || toInt(a.id) - toInt(b.id),
    ),
  }));

  return ordered.sort(
    (a, b) =>
      String(a.stationName).localeCompare(String(b.stationName), undefined, { numeric: true })
      || a.hour - b.hour,
  );
};

/**
 * Roll-up numbers for the KPI strip above the break table.
 */
export const summariseBreaks = (breaks = []) => {
  const active = breaks.filter(isBreakActive);
  const stations = new Set();
  const stationHours = new Set();
  let spotCapacity = 0;
  let durationSeconds = 0;

  breaks.forEach((breakItem) => {
    if (!breakItem) return;
    stations.add(stationKey(breakItem.station_id));
    stationHours.add(`${stationKey(breakItem.station_id)}|${toInt(breakItem.hour)}`);
    spotCapacity += Number(breakItem.max_spots) || 0;
    durationSeconds += Number(breakItem.max_duration) || 0;
  });

  return {
    total: breaks.length,
    active: active.length,
    inactive: breaks.length - active.length,
    stationCount: stations.size,
    hoursCovered: stationHours.size,
    spotCapacity,
    durationSeconds,
    averageDuration: breaks.length ? Math.round(durationSeconds / breaks.length) : 0,
  };
};
